import {
  Badge,
  BarList,
  Button,
  CommandPalette,
  ConfirmDialog,
  DataTable,
  DescriptionList,
  EmptyState,
  EntityAvatar,
  ErrorState,
  FilterBar,
  FilterSelect,
  formatCentsBRL,
  formatCompactCentsBRL,
  FormDialog,
  IdChip,
  Input,
  Kbd,
  Label,
  LoadingState,
  MetricCard,
  MoneyInput,
  PageHeader,
  PageTabs,
  SearchInput,
  SectionCard,
  SegmentedControl,
  Skeleton,
  Sparkline,
  Spinner,
  STATUS_TONE_SOLID,
  StatusBadge,
  type StatusTone,
  Switch,
  Tabs,
  TabsContent,
  Textarea,
  Timeline,
  TimeSeriesChart,
  TrendBadge,
  UsageMeter,
} from '@repo/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CreditCard,
  Gauge,
  Megaphone,
  MessageCircle,
  Palette,
  Plus,
  Search,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'

// Catálogo do design system (docs/design-system.md). Só existe em dev: serve
// para ver tokens e padrões lado a lado antes de usar numa tela.
export const Route = createFileRoute('/design-system')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw redirect({ to: '/' })
  },
  component: DesignSystemPage,
})

const SURFACES = [
  ['background', 'bg-background'],
  ['card', 'bg-card'],
  ['muted', 'bg-muted'],
  ['accent', 'bg-accent'],
  ['secondary', 'bg-secondary'],
  ['primary', 'bg-primary'],
] as const

const STRONG = [
  ['success', 'bg-success'],
  ['warning', 'bg-warning'],
  ['info', 'bg-info'],
  ['destructive', 'bg-destructive'],
  ['ai', 'bg-ai'],
] as const

const CHARTS = [
  ['chart-1', 'bg-chart-1'],
  ['chart-2', 'bg-chart-2'],
  ['chart-3', 'bg-chart-3'],
  ['chart-4', 'bg-chart-4'],
  ['chart-5', 'bg-chart-5'],
  ['chart-6', 'bg-chart-6'],
] as const

const TONES: { tone: StatusTone; label: string; use: string }[] = [
  { tone: 'success', label: 'Confirmado', use: 'deu certo' },
  { tone: 'warning', label: 'Pendente', use: 'espera alguém' },
  { tone: 'info', label: 'Enviando', use: 'em andamento' },
  { tone: 'destructive', label: 'Falhou', use: 'deu errado' },
  { tone: 'ai', label: 'IA ativa', use: 'IA ou automação' },
  { tone: 'neutral', label: 'Encerrado', use: 'sem estado' },
]

const PERIODS = [
  { value: '7', label: '7 dias' },
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
] as const

const TYPE_SCALE = [
  ['text-2xs', '11px', 'text-2xs'],
  ['text-xs', '12px', 'text-xs'],
  ['text-sm', '14px', 'text-sm'],
  ['text-base', '16px', 'text-base'],
  ['text-lg', '18px', 'text-lg'],
  ['text-xl', '20px', 'text-xl font-semibold'],
  ['text-2xl', '24px', 'text-2xl font-semibold'],
] as const

type Row = {
  name: string
  status: StatusTone
  label: string
  messages: number
}

const ROWS: Row[] = [
  {
    name: 'Canal comercial',
    status: 'success',
    label: 'Conectado',
    messages: 1840,
  },
  {
    name: 'Canal suporte',
    status: 'warning',
    label: 'QR expirado',
    messages: 312,
  },
  {
    name: 'Canal antigo',
    status: 'neutral',
    label: 'Desconectado',
    messages: 0,
  },
  {
    name: 'Canal pós-venda',
    status: 'success',
    label: 'Conectado',
    messages: 954,
  },
  {
    name: 'Canal eventos',
    status: 'info',
    label: 'Reconectando',
    messages: 77,
  },
]

// Série fictícia para os exemplos de gráfico (MRR em centavos).
const MRR_SERIES = Array.from({ length: 30 }, (_, index) => ({
  date: `2026-09-${String(index + 1).padStart(2, '0')}`,
  mrrCents: 4_200_000 + index * 21_000 + (index % 5) * 15_000,
}))

const STATUS_FILTER = [
  { value: 'success', label: 'Conectados' },
  { value: 'warning', label: 'Com atenção' },
  { value: 'neutral', label: 'Desconectados' },
] as const

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {description ? (
          <p className="text-muted-foreground text-sm">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function Swatches({
  items,
}: {
  items: readonly (readonly [string, string])[]
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {items.map(([name, className]) => (
        <div key={name} className="space-y-1.5">
          <div className={`h-12 rounded-md border ${className}`} />
          <p className="font-mono text-xs">{name}</p>
        </div>
      ))}
    </div>
  )
}

function DesignSystemPage() {
  const [period, setPeriod] = useState<string>('30')
  const [status, setStatus] = useState<StatusTone | 'all'>('all')
  const [loading, setLoading] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [price, setPrice] = useState<number | null>(149900)
  return (
    <div className="bg-muted/40 min-h-screen">
      <PageHeader
        icon={Palette}
        title="Design system"
        description="Tokens e padrões do @repo/ui. Regras em docs/design-system.md."
      />

      <main className="mx-auto max-w-6xl space-y-12 p-6">
        <Section title="Superfícies e marca">
          <Swatches items={SURFACES} />
        </Section>

        <Section
          title="Status"
          description="Um tom por estado. Use StatusBadge; para pontos e barras, STATUS_TONE_SOLID."
        >
          <Swatches items={STRONG} />
          <div className="bg-card flex flex-wrap gap-6 rounded-lg border p-4">
            {TONES.map(({ tone, label, use }) => (
              <div key={tone} className="space-y-1.5">
                <StatusBadge tone={tone}>{label}</StatusBadge>
                <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <span
                    className={`size-2 rounded-full ${STATUS_TONE_SOLID[tone]}`}
                  />
                  {tone}: {use}
                </p>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title="Gráficos"
          description="Séries na ordem. Toda série precisa de rótulo ou legenda. TimeSeriesChart para evolução no tempo; BarList para ranking e composição."
        >
          <Swatches items={CHARTS} />
          <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_0.6fr]">
            <SectionCard title="MRR por dia">
              <TimeSeriesChart
                data={MRR_SERIES}
                series={[{ key: 'mrrCents', label: 'MRR' }]}
                formatValue={formatCentsBRL}
                formatAxisValue={formatCompactCentsBRL}
                height={220}
              />
            </SectionCard>
            <SectionCard title="MRR por plano">
              <BarList
                items={[
                  {
                    key: 'pro',
                    label: 'Pro',
                    value: 2_091_000,
                    display: 'R$ 20.910,00',
                    hint: '30 clientes',
                  },
                  {
                    key: 'scale',
                    label: 'Scale',
                    value: 1_495_500,
                    display: 'R$ 14.955,00',
                    hint: '15 clientes',
                  },
                  {
                    key: 'starter',
                    label: 'Starter',
                    value: 793_000,
                    display: 'R$ 7.930,00',
                    hint: '20 clientes',
                  },
                ]}
              />
            </SectionCard>
          </div>
        </Section>

        <Section
          title="Tipografia"
          description="Inter; Geist Mono em font-mono."
        >
          <div className="bg-card divide-y rounded-lg border">
            {TYPE_SCALE.map(([name, size, className]) => (
              <div key={name} className="flex items-baseline gap-4 px-4 py-3">
                <span className="text-muted-foreground w-28 font-mono text-xs">
                  {name} · {size}
                </span>
                <span className={className}>
                  Enquanto seu vendedor digita, seu concorrente já fechou.
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Controles">
          <div className="bg-card space-y-6 rounded-lg border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button>Principal</Button>
              <Button variant="outline">Secundária</Button>
              <Button variant="secondary">Neutra</Button>
              <Button variant="ghost">Discreta</Button>
              <Button variant="destructive">Excluir</Button>
              <Button variant="link">Link</Button>
              <Button disabled>
                <Spinner />
                Salvando
              </Button>
              <Button size="icon" variant="outline" title="Adicionar">
                <Plus />
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ds-name">Nome</Label>
                <Input id="ds-name" placeholder="Fazenda Boa Vista" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-notes">Observações</Label>
                <Textarea id="ds-notes" placeholder="Opcional" rows={1} />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ds-ai" defaultChecked />
                <Label htmlFor="ds-ai">IA ativa</Label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge>Padrão</Badge>
                <Badge variant="secondary">Contador</Badge>
                <Badge variant="outline">Rótulo</Badge>
              </div>
            </div>
          </div>
        </Section>

        <Section
          title="PageHeader"
          description="bar no topo das páginas; plain dentro de um conteúdo com padding. Seção principal tem ícone; subpágina tem voltar."
        >
          <div className="overflow-hidden rounded-lg border">
            <PageHeader
              icon={Megaphone}
              title="Campanhas"
              description="Disparos em massa por canal"
              meta={<Badge variant="secondary">12</Badge>}
              actions={
                <Button>
                  <Plus />
                  Nova campanha
                </Button>
              }
            />
          </div>
          <div className="bg-card rounded-lg border p-6">
            <PageHeader
              variant="plain"
              title="Campanha de safra"
              description="Canal comercial"
              back={
                <Button variant="ghost" size="icon" title="Voltar">
                  <ArrowLeft />
                </Button>
              }
              meta={<StatusBadge tone="info">Enviando</StatusBadge>}
            />
          </div>
        </Section>

        <Section title="Estados">
          <div className="grid gap-4 lg:grid-cols-2">
            <EmptyState
              icon={Users}
              title="Nenhum lead ainda"
              description="Importe uma planilha ou crie o primeiro lead."
              action={
                <Button>
                  <Plus />
                  Novo lead
                </Button>
              }
            />
            <div className="bg-card rounded-lg border">
              <ErrorState onRetry={() => toast('Tentando de novo')} />
            </div>
            <div className="bg-card rounded-lg border">
              <LoadingState />
            </div>
            <div className="bg-card space-y-2 rounded-lg border p-4">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </Section>

        <Section title="Métricas e tabela">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              label="MRR"
              value="R$ 48.795"
              hint="TrendBadge diz se a mudança é boa; Sparkline mostra a forma"
              trend={<TrendBadge value={0.12} comparedTo="vs. mês passado" />}
              chart={
                <Sparkline values={MRR_SERIES.map((point) => point.mrrCents)} />
              }
              icon={Wallet}
            />
            <MetricCard
              label="Custo de IA"
              value="R$ 1.920"
              hint="goodWhen down: custo subindo é ruim"
              trend={<TrendBadge value={0.08} goodWhen="down" />}
              icon={Megaphone}
            />
            <MetricCard
              label="Falhas de envio"
              value="27"
              hint="últimas 24 h"
              icon={AlertTriangle}
              tone="destructive"
            />
            <MetricCard
              label="Em atraso"
              value="R$ 2.990"
              hint="tone warning: pede atenção"
              icon={Users}
              tone="warning"
            />
            <MetricCard
              label="Carregando"
              value=""
              hint="loading mantém o tamanho"
              loading
            />
            <MetricCard
              label="Variação neutra"
              value="1.284"
              hint="goodWhen neutral: só informa"
              trend={<TrendBadge value={-0.04} goodWhen="neutral" />}
              icon={Users}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="ds-loading"
              checked={loading}
              onCheckedChange={setLoading}
            />
            <Label htmlFor="ds-loading">Tabela carregando</Label>
          </div>
          <DataTable
            rows={ROWS}
            loading={loading}
            getRowKey={(row) => row.name}
            empty="Nenhum canal"
            variant="card"
            pageSize={3}
            defaultSort={{ key: 'messages', direction: 'desc' }}
            onRowClick={(row) => toast(`Abrir ${row.name}`)}
            columns={[
              {
                key: 'name',
                header: 'Canal',
                sortValue: (row) => row.name,
                render: (row) => row.name,
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <StatusBadge tone={row.status}>{row.label}</StatusBadge>
                ),
              },
              {
                key: 'messages',
                header: 'Mensagens',
                align: 'right',
                sortValue: (row) => row.messages,
                render: (row) => row.messages.toLocaleString('pt-BR'),
              },
            ]}
          />
        </Section>

        <Section
          title="Seções e filtros"
          description="SectionCard organiza uma página em blocos; formulários levam as ações no rodapé. Filtros ficam numa barra acima do conteúdo que filtram."
        >
          <FilterBar
            active={status !== 'all'}
            onClear={() => setStatus('all')}
            end="5 de 5"
          >
            <SegmentedControl
              label="Período"
              options={PERIODS}
              value={period}
              onChange={setPeriod}
            />
            <SearchInput
              className="max-w-xs"
              placeholder="Buscar organização"
            />
            <FilterSelect
              label="Status"
              value={status}
              onChange={setStatus}
              options={STATUS_FILTER}
            />
          </FilterBar>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <SectionCard
              title="Canais"
              description="Conexões de WhatsApp desta organização."
              actions={
                <Button size="sm">
                  <Plus />
                  Novo canal
                </Button>
              }
              contentClassName="p-0"
            >
              <DataTable
                variant="flush"
                rows={ROWS}
                getRowKey={(row) => row.name}
                empty="Nenhum canal"
                columns={[
                  { key: 'name', header: 'Canal', render: (row) => row.name },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (row) => (
                      <StatusBadge tone={row.status}>{row.label}</StatusBadge>
                    ),
                  },
                ]}
              />
            </SectionCard>
            <SectionCard
              title="Conceder cortesia"
              description="Formulário: campos no corpo, ação principal no rodapé."
              footer={<Button>Aplicar</Button>}
            >
              <div className="space-y-2">
                <Label htmlFor="ds-end">Fim da cortesia</Label>
                <Input id="ds-end" type="date" />
              </div>
            </SectionCard>
            <SectionCard
              tone="destructive"
              className="lg:col-span-2"
              title="Zona de perigo"
              description="Ações irreversíveis ficam isoladas no fim da página."
              footer={
                <Button variant="destructive">Excluir organização</Button>
              }
            />
          </div>
        </Section>

        <Section
          title="IDs e busca"
          description="IdChip para IDs técnicos (copiar e abrir na origem). CommandPalette com ⌘K para pular para qualquer tela ou registro."
        >
          <div className="bg-card flex flex-wrap items-center gap-6 rounded-lg border p-4">
            <IdChip value="sub_1QxYz2AbCdEfGhIjKlMnOp" />
            <IdChip
              value="cus_Q8w7e6r5t4y3u2i1"
              href="https://dashboard.stripe.com/test/customers/cus_Q8w7e6r5t4y3u2i1"
              hrefLabel="Stripe"
            />
            <Button
              variant="outline"
              size="sm"
              className="text-muted-foreground w-56 justify-start"
              onClick={() => setSearchOpen(true)}
            >
              <Search />
              <span className="flex-1 text-left">Buscar…</span>
              <Kbd>⌘K</Kbd>
            </Button>
          </div>
          <CommandPalette
            open={searchOpen}
            onOpenChange={setSearchOpen}
            groups={[
              {
                heading: 'Canais',
                items: ROWS.map((row) => ({
                  id: row.name,
                  label: row.name,
                  hint: row.label,
                  onSelect: () => toast(`Abrir ${row.name}`),
                })),
              },
            ]}
          />
        </Section>

        <Section
          title="Registros e ações"
          description="DescriptionList para os dados de um registro; UsageMeter para uso contra limite. Cada ação com formulário é um botão que abre um FormDialog: nunca vários formulários abertos lado a lado. MoneyInput mostra reais e guarda centavos."
        >
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <SectionCard
              title="Plano atual"
              actions={
                <Button size="sm" onClick={() => setFormOpen(true)}>
                  Novo contrato
                </Button>
              }
            >
              <DescriptionList
                items={[
                  { label: 'Plano', value: 'Enterprise' },
                  { label: 'Preço', value: 'R$ 1.499,00/mês' },
                  { label: 'Período', value: '1 de out. a 31 de out.' },
                  { label: 'Mudança agendada', value: null },
                ]}
              />
            </SectionCard>
            <SectionCard title="Limites e uso">
              <div className="space-y-4">
                <UsageMeter label="Mensagens" used={8200} limit={10000} />
                <UsageMeter label="Canais" used={2} limit={2} />
                <UsageMeter label="Membros" used={4} limit={10} />
              </div>
            </SectionCard>
          </div>
          <FormDialog
            open={formOpen}
            onOpenChange={setFormOpen}
            title="Novo contrato"
            description="Formulário de uma ação: só os campos dela, ação principal no rodapé."
            submitLabel="Criar contrato"
            submitDisabled={price === null}
            onSubmit={() => {
              toast.success('Contrato criado')
              setFormOpen(false)
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="ds-price">Preço mensal</Label>
              <MoneyInput id="ds-price" value={price} onChange={setPrice} />
            </div>
          </FormDialog>
        </Section>

        <Section
          title="Página de detalhe"
          description="PageTabs presas ao cabeçalho (PageLayout tabs), com ícone, contagem e ponto de alerta. EntityAvatar mostra o logo ou, sem logo, o ícone do tipo. Timeline para trilhas de auditoria lidas por pessoas."
        >
          <Tabs
            defaultValue="geral"
            className="bg-card gap-0 overflow-hidden rounded-lg border"
          >
            <div className="border-b">
              <PageHeader
                variant="plain"
                className="px-6 pt-4 pb-2"
                title="Fazenda Boa Vista"
                description="fazenda-boa-vista"
                avatar={
                  <EntityAvatar
                    name="Fazenda Boa Vista"
                    icon={Building2}
                    size="md"
                  />
                }
                meta={<StatusBadge tone="success">Ativa</StatusBadge>}
              />
              <PageTabs
                label="Seções"
                items={[
                  { value: 'geral', label: 'Visão geral', icon: Gauge },
                  {
                    value: 'assinatura',
                    label: 'Assinatura',
                    icon: CreditCard,
                    alert: 'warning',
                  },
                  { value: 'equipe', label: 'Equipe', icon: Users, count: 12 },
                  {
                    value: 'canais',
                    label: 'Canais',
                    icon: MessageCircle,
                    count: 3,
                    alert: 'destructive',
                  },
                  {
                    value: 'conformidade',
                    label: 'Conformidade',
                    icon: ShieldCheck,
                  },
                ]}
              />
            </div>
            {['geral', 'assinatura', 'equipe', 'canais', 'conformidade'].map(
              (value) => (
                <TabsContent key={value} value={value} className="p-6">
                  <Timeline
                    items={[
                      {
                        id: '1',
                        icon: ShieldCheck,
                        tone: 'success',
                        title: 'RD Station liberada',
                        description: 'Maria · “DPA assinado em 01/10”',
                        meta: 'há 2 dias',
                      },
                      {
                        id: '2',
                        icon: ShieldCheck,
                        tone: 'warning',
                        title:
                          'Guarda de consentimento: Bloquear → Só observar',
                        description: 'João · “Teste de campanha de safra”',
                        meta: 'há 1 semana',
                      },
                    ]}
                  />
                </TabsContent>
              )
            )}
          </Tabs>
        </Section>

        <Section title="Confirmação">
          <div className="flex gap-2">
            <ConfirmDialog
              trigger={<Button variant="outline">Arquivar canal</Button>}
              title="Arquivar canal?"
              description="Cadências presas ao canal são canceladas e as credenciais apagadas."
              confirmLabel="Arquivar"
              destructive
              onConfirm={() => toast.success('Arquivado')}
            />
            <Button onClick={() => toast.success('Salvo')}>Toast</Button>
          </div>
        </Section>
      </main>
    </div>
  )
}
